import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ENDPOINTS } from '@shared/constants/endpoints.constants';
import { ApiResponse } from '@shared/types/api-response.type';
import { map, Observable } from 'rxjs';
import { EmailChatMessage, EmailChatResult } from '../entity/emails.service';

/** A named assistant conversation of the current user. */
export interface AssistantThread {
    id: string;
    title: string;
    modifiedAt: string;
}

/** One visible turn of the global assistant — assistant turns may carry the grounding sources. */
export interface AssistantChatTurn {
    role: 'user' | 'assistant';
    content: string;
    sources?: { id: string; kind: string; title: string }[];
}

@Injectable({
    providedIn: 'root'
})
export class AssistantService {

    // -----------------------------------------------------------------------------------------------------
    // @ DEPENDENCIES
    // -----------------------------------------------------------------------------------------------------

    private readonly httpClient = inject(HttpClient);

    // -----------------------------------------------------------------------------------------------------
    // @ INSTANCE VARIABLES
    // -----------------------------------------------------------------------------------------------------

    // -----------------------------------------------------------------------------------------------------
    // @ PUBLIC METHODS
    // -----------------------------------------------------------------------------------------------------

    /** The persisted chat of the current user for one thread (null = legacy history), oldest first. */
    getHistory(threadId?: string | null): Observable<AssistantChatTurn[]> {
        return this.httpClient
            .get<ApiResponse<AssistantChatTurn[]>>(`${ENDPOINTS.assistant.base}/chat`,
                { params: threadId ? { threadId } : {} })
            .pipe(map((response) => response.data ?? []));
    }

    /** The current user's threads, newest first. */
    getThreads(): Observable<AssistantThread[]> {
        return this.httpClient
            .get<ApiResponse<AssistantThread[]>>(`${ENDPOINTS.assistant.base}/threads`)
            .pipe(map((response) => response.data ?? []));
    }

    /** Starts a fresh thread — the title usually is the first question. */
    createThread(title: string): Observable<AssistantThread> {
        return this.httpClient
            .post<ApiResponse<AssistantThread>>(`${ENDPOINTS.assistant.base}/threads`, { title })
            .pipe(map((response) => response.data));
    }

    /** Deletes one thread including its turns. */
    deleteThread(threadId: string): Observable<void> {
        return this.httpClient.delete<void>(`${ENDPOINTS.assistant.base}/threads/${threadId}`);
    }

    /** Forgets the user's chat of one thread (null = legacy history). */
    clearHistory(threadId?: string | null): Observable<void> {
        return this.httpClient.delete<void>(`${ENDPOINTS.assistant.base}/chat`,
            { params: threadId ? { threadId } : {} });
    }

    /**
     * One turn of the global assistant — the same tool loop as the email chat, without an email.
     * The server is stateless: the whole visible conversation travels with every turn.
     * @param messages - The visible conversation (roles user / assistant).
     * @returns {Observable<EmailChatResult>} The reply plus its sources.
     */
    chat(messages: EmailChatMessage[], threadId?: string | null): Observable<EmailChatResult> {
        return this.httpClient
            .post<ApiResponse<EmailChatResult>>(`${ENDPOINTS.assistant.base}/chat`, { messages },
                { params: threadId ? { threadId } : {} })
            .pipe(map((response) => response.data));
    }
}
